import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import supabase from '../supabase/supabase';

const AdminAuthContext = createContext(null);
const MEMBERSHIP_TABLE = 'guide_memberships_1789700000000';

export function AdminAuthProvider({children}) {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [membership, setMembership] = useState({
    userId: null,
    status: 'loading',
    guideId: ''
  });

  useEffect(() => {
    let active = true;
    const {data: {subscription}} = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        if (!active) return;
        setSession(nextSession);
        setAuthError('');
        setAuthLoading(false);
      }
    );

    supabase.auth.getSession()
      .then(({data, error}) => {
        if (!active) return;
        setSession(error ? null : data.session);
        setAuthError(error?.message || '');
        setAuthLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        setSession(null);
        setAuthError(error.message || 'Unable to restore the Supabase session.');
        setAuthLoading(false);
      });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const userId = session?.user?.id;

    if (!userId) {
      setMembership({userId: null, status: 'signed-out', guideId: ''});
      return undefined;
    }

    let active = true;
    setMembership({userId, status: 'loading', guideId: ''});

    supabase
      .from(MEMBERSHIP_TABLE)
      .select('guide_id')
      .eq('user_id', userId)
      .then(({data, error}) => {
        if (!active) return;
        if (error) {
          setMembership({userId, status: 'error', guideId: ''});
          return;
        }

        const guideId = data?.length === 1 ? data[0].guide_id : '';
        if (data?.length !== 1 || typeof guideId !== 'string' || !guideId.trim()) {
          setMembership({userId, status: 'denied', guideId: ''});
          return;
        }

        setMembership({userId, status: 'allowed', guideId});
      })
      .catch(() => {
        if (active) setMembership({userId, status: 'error', guideId: ''});
      });

    return () => {
      active = false;
    };
  }, [session?.user?.id]);

  const signIn = useCallback(async (email, password) => {
    setAuthError('');
    try {
      const {error} = await supabase.auth.signInWithPassword({email, password});
      if (error) setAuthError(error.message);
      return {error};
    } catch (error) {
      setAuthError(error.message || 'Unable to sign in.');
      return {error};
    }
  }, []);

  const signOut = useCallback(async () => {
    setAuthError('');
    try {
      const {error} = await supabase.auth.signOut();
      if (error) setAuthError(error.message);
      return {error};
    } catch (error) {
      setAuthError(error.message || 'Unable to sign out.');
      return {error};
    }
  }, []);

  const value = useMemo(() => ({
    session,
    user: session?.user || null,
    authLoading,
    authError,
    setAuthError,
    membership,
    guideId: membership.status === 'allowed' ? membership.guideId : '',
    signIn,
    signOut
  }), [session, authLoading, authError, membership, signIn, signOut]);

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used inside AdminAuthProvider.');
  }
  return context;
}